import React, { useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Card, Button, Input, Alert } from '../ui';
import { FiMail, FiLock, FiArrowLeft, FiCheck, FiKey } from 'react-icons/fi';

const ForgotPassword = ({ onBack }) => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [step, setStep] = useState('email');
  const [verificationCode, setVerificationCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordStrength, setPasswordStrength] = useState(0);

  const navigate = useNavigate();

  const baseURL =
    process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:5000/api';

  const checkPasswordStrength = (pass) => {
    let strength = 0;
    if (pass.length >= 8) strength += 1;
    if (/[A-Z]/.test(pass)) strength += 1;
    if (/[0-9]/.test(pass)) strength += 1;
    if (/[^A-Za-z0-9]/.test(pass)) strength += 1;
    setPasswordStrength(strength);
  };

  const handlePasswordChange = (e) => {
    const newPass = e.target.value;
    setNewPassword(newPass);
    checkPasswordStrength(newPass);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      setError('Please enter your email address');
      return;
    }

    setLoading(true);
    setError('');
    setMessage('');

    try {
      await axios.post(`${baseURL}/auth/forgot-password`, {
        email: email.trim(),
        type: 'reset'
      });

      setStep('verify');
      setMessage('');
    } catch (err) {
      console.error('Password recovery error:', err);
      setError(
        err.response?.data?.message ||
          'An error occurred while sending the recovery email. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    const code = verificationCode.trim();
    if (!code) {
      setError('Please enter the verification code');
      return;
    }
    if (code.length < 6) {
      setError('Please enter the full verification code from your email');
      return;
    }

    // Do not call /auth/verify-otp here: Supabase invalidates the OTP after one
    // successful verifyOtp. Password reset uses the same code once in POST
    // /auth/reset-password, which verifies the OTP and updates the password in
    // Supabase Auth (admin.updateUserById).
    setStep('newPassword');
    setMessage('');
    setError('');
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }

    setLoading(true);
    setError('');
    setMessage('');

    try {
      await axios.post(`${baseURL}/auth/reset-password`, {
        email: email.trim(),
        otp: verificationCode.trim(),
        newPassword: newPassword,
        type: 'reset'
      });

      setMessage('Password reset successful! Redirecting to login...');

      setTimeout(() => {
        navigate('/login');
      }, 2000);
    } catch (err) {
      console.error('Password reset error:', err);
      setError(
        err.response?.data?.message ||
          'An error occurred while resetting your password. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setStep('email');
    setEmail('');
    setMessage('');
    setError('');
    setVerificationCode('');
    setNewPassword('');
    setConfirmPassword('');
    setPasswordStrength(0);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-white dark:from-gray-900 dark:to-gray-800 flex items-center justify-center p-4">
      <Card className="max-w-md w-full" shadow="lg" padding="none">
        <Card.Content className="p-8">
          <div className="text-center mb-8">
            <div className="w-16 h-16 mx-auto mb-4 bg-amber-100 dark:bg-amber-900/30 rounded-full flex items-center justify-center">
              <FiLock className="w-8 h-8 text-amber-600 dark:text-amber-400" />
            </div>
            <h1 className="text-2xl font-serif text-primary-900 dark:text-white mb-2">
              {step === 'verify'
                ? 'Enter Verification Code'
                : step === 'newPassword'
                  ? 'Set New Password'
                  : 'Reset Password'}
            </h1>
            <p className="font-literary text-primary-600 dark:text-gray-300">
              {step === 'verify'
                ? `Enter the code sent to ${email}`
                : step === 'newPassword'
                  ? 'Please enter your new password'
                  : 'Enter your email to receive a verification code'}
            </p>
          </div>

          {message && step !== 'verify' && step !== 'newPassword' && (
            <div className="mb-6">
              <Alert type="success">
                <div className="flex flex-col">
                  <span>{message}</span>
                  <button
                    type="button"
                    onClick={resetForm}
                    className="text-sm font-medium underline mt-2 text-left"
                  >
                    Send another recovery email
                  </button>
                </div>
              </Alert>
            </div>
          )}

          {message && step === 'newPassword' && (
            <div className="mb-6">
              <Alert type="success">{message}</Alert>
            </div>
          )}

          {error && (
            <div className="mb-6">
              <Alert type="error" onClose={() => setError('')}>
                {error}
              </Alert>
            </div>
          )}

          {step === 'email' && !message && (
            <form onSubmit={handleSubmit} className="space-y-6">
              <Input
                label="Email Address"
                type="email"
                icon={FiMail}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email address"
                required
                disabled={loading}
              />

              <div className="p-4 rounded-lg border bg-amber-50 border-amber-200 dark:bg-amber-900/20 dark:border-amber-800">
                <div className="flex items-center space-x-3">
                  <span className="text-xl text-amber-600 dark:text-amber-400">
                    <FiLock />
                  </span>
                  <div>
                    <p className="font-medium text-primary-900 dark:text-white">
                      Password reset
                    </p>
                    <p className="text-sm text-primary-600 dark:text-gray-400">
                      You&apos;ll receive a verification code to set a new password
                    </p>
                  </div>
                </div>
              </div>

              <Button
                type="submit"
                variant="primary"
                className="w-full bg-amber-600 hover:bg-amber-700 text-white border-transparent"
                loading={loading}
                disabled={loading}
              >
                {loading ? 'Sending...' : 'Send Verification Code'}
              </Button>
            </form>
          )}

          {step === 'verify' && !message && (
            <form onSubmit={handleVerifyCode} className="space-y-6">
              <Input
                label="Verification Code"
                type="text"
                icon={FiKey}
                value={verificationCode}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, '');
                  if (value.length <= 8) {
                    setVerificationCode(value);
                    setError('');
                  }
                }}
                placeholder="Enter verification code"
                required
                disabled={loading}
                className="text-center text-xl tracking-wide font-mono"
                maxLength={8}
              />

              <Button
                type="submit"
                variant="primary"
                className="w-full bg-amber-600 hover:bg-amber-700 text-white border-transparent"
                loading={loading}
                disabled={loading}
              >
                {loading ? 'Verifying...' : 'Verify Code'}
              </Button>

              <div className="text-center">
                <button
                  type="button"
                  onClick={handleSubmit}
                  className="text-sm text-primary-600 hover:text-primary-800 dark:text-gray-400 dark:hover:text-gray-200"
                  disabled={loading}
                >
                  Resend Code
                </button>
              </div>
            </form>
          )}

          {step === 'newPassword' && !message && (
            <form onSubmit={handlePasswordReset} className="space-y-6">
              <div>
                <Input
                  label="New Password"
                  type="password"
                  icon={FiLock}
                  value={newPassword}
                  onChange={handlePasswordChange}
                  placeholder="Enter new password"
                  required
                  disabled={loading}
                  showPasswordToggle
                />
                {newPassword && (
                  <div className="mt-2 flex space-x-1">
                    {[1, 2, 3, 4].map((level) => (
                      <div
                        key={level}
                        className={`h-1 flex-1 rounded-full transition-colors duration-300 ${
                          passwordStrength >= level
                            ? passwordStrength <= 2
                              ? 'bg-red-400'
                              : passwordStrength === 3
                                ? 'bg-amber-400'
                                : 'bg-green-400'
                            : 'bg-gray-200 dark:bg-gray-700'
                        }`}
                      />
                    ))}
                  </div>
                )}
                {newPassword && (
                  <p className="text-xs text-right mt-1 text-gray-500 dark:text-gray-400">
                    Strength:{' '}
                    {['Weak', 'Fair', 'Good', 'Strong'][passwordStrength - 1] || 'Weak'}
                  </p>
                )}
              </div>

              <Input
                label="Confirm Password"
                type="password"
                icon={FiCheck}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm new password"
                required
                disabled={loading}
                showPasswordToggle
              />

              <Button
                type="submit"
                variant="primary"
                className="w-full bg-amber-600 hover:bg-amber-700 text-white border-transparent"
                loading={loading}
                disabled={loading}
              >
                {loading ? 'Resetting Password...' : 'Reset Password'}
              </Button>
            </form>
          )}

          <div className="mt-8 text-center space-y-2">
            <button
              type="button"
              onClick={resetForm}
              className="text-primary-500 hover:text-primary-700 dark:text-gray-400 dark:hover:text-gray-200 text-sm font-medium transition-colors block w-full"
            >
              Start over
            </button>
            <button
              type="button"
              onClick={onBack}
              className="text-primary-500 hover:text-primary-700 dark:text-gray-400 dark:hover:text-gray-200 text-sm font-medium transition-colors flex items-center justify-center mx-auto"
            >
              <FiArrowLeft className="mr-2" />
              Back to Login
            </button>
          </div>
        </Card.Content>
      </Card>
    </div>
  );
};

export default ForgotPassword;
